import {useField} from 'formik';
// import {Select} from 'formik-mui';
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import clsx from 'clsx';
import React, {useEffect, useState} from 'react';

import {Search} from '@mui/icons-material';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';
import {FieldValue, SelectAutocompleteField} from './AutoFieldNew';
import {getSelectOptionValue} from './AutoSelectNew';
import './AutoSelectStyles.css';
import {AutoFieldDebug} from './AutoFieldDebug';
import {SaltusAny} from '../../utils/SaltusAny';

interface Props {
  field: SelectAutocompleteField;
  controlledValue: FieldValue | undefined;
  setControlledValue: React.Dispatch<React.SetStateAction<FieldValue | undefined>>;
  handleFieldSubmit: (value?: FieldValue) => void;
  loading?: boolean;
  disabled: boolean;
  onBlur?: (x: SaltusAny) => SaltusAny;
  readOnly: boolean;
  error?: boolean;
}

export default function AutoSelectAutocomplete({
  field,
  controlledValue,
  setControlledValue,
  handleFieldSubmit,
  loading,
  onBlur,
  disabled,
  readOnly,
  error,
}: Props) {
  const [formikField, formikMeta, formikHelpers] = useField(field.id);
  const [dropdownOpen, setDropdownOpen] = useState<undefined | boolean>(
    field.serverSearch ? false : undefined
  );
  const [inputFormikField, _, inputFormikHelpers] = useField<string | undefined>(
    field.serverSearch?.inputFormKey
      ? field.serverSearch?.inputFormKey
      : '__INPUT_FORM__KEY__'
  );
  // const [searchTerm, setSearchTerm] = useState('');
  const {options} = field;

  useEffect(() => {
    if (field.serverSearch && inputFormikField.value) {
      setDropdownOpen(true);
    }
  }, [field.serverSearch, inputFormikField.value]);
  if (!field.options.length && !field.disabled && !field.serverSearch) {
    return (
      <Box flex={1}>
        <AutoFieldDebug>{field.id}</AutoFieldDebug>
        <TextField
          fullWidth
          disabled
          error={error}
          size={field.size || 'small'}
          role="combobox"
          inputProps={{
            id: field.id,
            readOnly: field.readOnly,
            'data-select-input': field.label,
          }}
          label={`${field.label} (loading...)`}
          variant="outlined"
        />
      </Box>
    );
  }
  return (
    <FormControl
      key={field.id}
      className={clsx({
        'select-readOnly': readOnly,
        readOnly,
        editable: !readOnly,
      })}
      fullWidth
      error={error || !!formikMeta.error}
      size={field.size || 'small'}
      id={field.id}
    >
      <AutoFieldDebug>
        {field.id} /
        {field.serverSearch ? <> Input value: {inputFormikField.value}</> : null}
      </AutoFieldDebug>
      <select
        onChange={() => {}} // used to shut up MUI in the console
        style={{display: 'none'}}
        value={(controlledValue as any) || field.initialValue}
        data-test={field.label}
        disabled={field.disabled || disabled || readOnly}
      >
        {options.map((option) => {
          const key = getSelectOptionValue(option);
          return <option key={key}>{option.label}</option>;
        })}
      </select>
      <Autocomplete
        key={field.id}
        id={field.id}
        style={{color: 'black'}}
        data-testmui={field.label}
        options={options}
        autoFocus={dropdownOpen}
        filterOptions={field.serverSearch ? (x) => x : undefined}
        open={dropdownOpen}
        onClose={() => (field.serverSearch ? setDropdownOpen(false) : undefined)}
        onOpen={() => (field.serverSearch ? setDropdownOpen(true) : undefined)}
        size={field.size || 'small'}
        disabled={field.disabled || disabled || readOnly}
        loading={loading}
        data-loading={options.length ? 'false' : 'true'}
        loadingText={field.serverSearch ? 'Loading...' : `${field.label}`}
        value={formikField.value || null}
        // inputValue={field.serverSearch?.inputFormKey ? inputFormikField.value : undefined}
        inputValue={field.serverSearch ? inputFormikField.value : undefined}
        onInputChange={
          field.serverSearch
            ? (event, value, reason) => {
                if (reason === 'input') {
                  inputFormikHelpers.setValue(value);
                  field.serverSearch?.fn?.(value);
                }
              }
            : undefined
        }
        groupBy={
          field.groupBy ||
          ((o) => {
            return o.group;
          })
        }
        isOptionEqualToValue={(option, currValue) => {
          // if (isObject(currValue) && isObject(option.value)) {
          //   // if using objects, use ids
          //   const isSelected = option.value.id === (currValue as any).id;
          //   return isSelected;
          // }
          const isSelected = option.value === (currValue as unknown as string);
          return isSelected;
        }}
        getOptionLabel={(option) => {
          if ((option as any) === 'Undefined') {
            return '';
          }
          const selectedOption = field.options.find(
            (o) => o.value === (typeof option === 'object' ? option.value : option)
          );
          return selectedOption?.label || '';
        }}
        // onChange={(_e: SaltusAny, x) => {
        //   formikHelpers.setValue(x?.value);
        //   setDropdownOpen(false);
        // }}
        onChange={(_e: SaltusAny, x) => {
          setDropdownOpen(false);
          if (readOnly) {
            return;
          }
          if (field.requireSubmit) {
            setControlledValue(x?.value as string);
            formikHelpers.setValue(x?.value);
          } else {
            setControlledValue(x?.value as string);
            formikHelpers.setValue(x?.value);
          }
          if (onBlur) {
            onBlur({});
          }
        }}
        renderInput={(params) => {
          return (
            <TextField
              key={field.id}
              error={error || !!formikMeta.error}
              role="combobox"
              {...params}
              inputProps={{
                id: field.id,
                readOnly: field.readOnly,
                'data-select-input': field.label,
                ...params.inputProps,
              }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{
                ...params.InputProps,
                startAdornment: field.searchIcon ? (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ) : null,
              }}
              label={field.label}
              variant="outlined"
            />
          );
        }}
      />
      {formikMeta.touched && formikMeta.error ? (
        <Typography
          color="error"
          variant="body2"
          style={{fontSize: '0.7rem', marginTop: 4, marginLeft: 8}}
        >
          {formikMeta.error}
        </Typography>
      ) : null}
      {field.requireSubmit && (controlledValue || formikField.value) && (
        <Box mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleFieldSubmit(controlledValue || formikField.value)}
          >
            {loading ? 'Loading...' : 'Confirm'}
          </Button>
        </Box>
      )}
      <ShowIfDebugWrapper debugKey="forms">
        {formikField.value && String(formikField.value).length === 36
          ? formikField.value
          : ''}
      </ShowIfDebugWrapper>
    </FormControl>
  );
}
